import { Provider } from 'react-redux';
import React, { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import * as R from 'ramda';
import CssBaseline from '@mui/material/CssBaseline';
import { getLocale } from './selectors/router';
import configureStore, { history } from './configureStore';
import {
  FormattedMessage,
  I18nProvider,
  initialize as initializeI18n,
} from './i18n';
import { initialize as initializeAnalytics } from './utils/analytics';
import initializeOidc from './oidc/init.js';
import { ThemeProvider } from './theme';
import searchApi from './api/search';
import shareApi from './api/share';
import { search, i18n, theme } from './lib/settings';
import ErrorBoundary from './components/error-boundary';
import Helmet from './components/helmet';
import meta from '../../package.json';
import { QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Route, Routes } from 'react-router';
import App from './components/app';
import { ReduxRouter } from '@lagunovsky/redux-react-router';
import * as Sentry from '@sentry/react';
import { getBasename } from './utils/index.js';
import { Loader } from './components/vis/loader.jsx';

console.info(`${meta.name}@${meta.version}`); // eslint-disable-line no-console

const Search = lazy(() => import('./components/search/search.jsx'));
const Vis = lazy(() => import('./components/vis.jsx'));
const Share = lazy(() => import('./share/share.jsx'));
const NotFound = lazy(() => import('./components/not-found.jsx'));

const initSentry = () => {
  if (R.isNil(window.CONFIG.sentryDSN)) return;

  Sentry.init({
    dsn: window.CONFIG.sentryDSN,
    environment: window.CONFIG.sentryEnv,
    release: window.CONFIG.sentryRelease,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    enableErrorHandling: false,
    autoSessionTracking: false,
  });
};

const initQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        retry: false,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
    },
    queryCache: new QueryCache({
      onSuccess: (data, query) => {
        if (!R.is(Function, query?.meta?.successHandler)) return;
        query.meta.successHandler({ data, queryKey: query.meta.queryKey });
      },
      onError: (error, query) => {
        if (!R.is(Function, query?.meta?.errorHandler)) return;
        /* if (error.code === 'E_JSON_PARSE' && R.isNil(spaceId)) {
          // redirect to invalid page with proper arg (ie invalid space)
          // when router will be full setup
        }*/
        query.meta.errorHandler({ error, queryKey: query.meta.queryKey });
        /*if (error?.response?.status === 401 && hasExternalAuth)
          dispatch(failedExtAuth(spaceId)); */
      },
    }),
  });
};

const run = async () => {
  const initialState = {};

  initSentry();
  const queryClient = initQueryClient();
  const store = configureStore(initialState);
  const locale = getLocale(store.getState());

  searchApi.setConfig(search);
  shareApi.setConfig(window.SETTINGS?.share);
  initializeI18n(i18n, locale);
  await initializeOidc({ store });
  initializeAnalytics({ locale });

  createRoot(document.getElementById('root')).render(
    <StrictMode>
      <ErrorBoundary id="root">
        <Provider store={store}>
          <ThemeProvider theme={theme}>
            <I18nProvider messages={window.I18N}>
              <Helmet />
              <CssBaseline />
              <QueryClientProvider client={queryClient}>
                <ReduxRouter history={history} basename={getBasename()}>
                  <App>
                    <ErrorBoundary id="app">
                      <Suspense
                        fallback={
                          <Loader
                            message={<FormattedMessage id="de.route.loading" />}
                            styles={{ marginTop: 176, marginRight: 0 }}
                          />
                        }
                      >
                        <Routes>
                          <Route path="/" element={<Search />} />
                          <Route path="/vis" element={<Vis />} />
                          <Route path="/share" element={<Share />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                      <ReactQueryDevtools initialIsOpen={true} />
                    </ErrorBoundary>
                  </App>
                </ReduxRouter>
              </QueryClientProvider>
            </I18nProvider>
          </ThemeProvider>
        </Provider>
      </ErrorBoundary>
    </StrictMode>,
  );
};

run();
